import { create } from "zustand";
import { api } from "../lib/api";
import { offlineMutate, tempId } from "../lib/offlineMutate";

export const useTasksStore = create((set, get) => ({
  tasks: [],
  lists: [],
  users: [],
  onlyMine: false,
  loading: false,

  // List-view UX state (persisted per session)
  sortBy: "manual", // manual | deadline | priority | assignee | created
  groupBy: "date", // date | list | priority
  showCompleted: false,
  showRecurring: false,

  setOnlyMine(v) {
    set({ onlyMine: v });
  },
  setSortBy(v) {
    set({ sortBy: v });
  },
  setGroupBy(v) {
    set({ groupBy: v });
  },
  setShowCompleted(v) {
    set({ showCompleted: v });
  },
  setShowRecurring(v) {
    set({ showRecurring: v });
  },

  async fetchLists(projectId) {
    const { data } = await api.get("/lists", { params: { project_id: projectId } });
    set({ lists: data });
    return data;
  },

  async fetchTasks(projectId) {
    set({ loading: true });
    const params = { project_id: projectId };
    if (get().onlyMine) params.only_mine = true;
    const { data } = await api.get("/tasks", { params });
    set({ tasks: data, loading: false });
    return data;
  },

  async fetchUsers(assignableOnly = false) {
    const params = assignableOnly ? { assignable: true } : {};
    const { data } = await api.get("/users", { params });
    set({ users: data });
    return data;
  },

  async updateSubtasks(taskId, subtasks) {
    const { data } = await api.patch(`/tasks/${taskId}/subtasks`, { subtasks });
    set((s) => ({
      tasks: s.tasks.map((t) =>
        t.id === taskId
          ? {
              ...data,
              comment_count: data.comment_count ?? t.comment_count ?? 0,
              attachment_count: data.attachment_count ?? t.attachment_count ?? 0,
            }
          : t,
      ),
    }));
    return data;
  },

  async createTask(payload) {
    const nowIso = new Date().toISOString();
    const { data, offline } = await offlineMutate({
      url: "/tasks",
      method: "POST",
      body: payload,
      buildOptimistic: () => ({
        id: tempId("task"),
        ...payload,
        status: payload.status || "todo",
        priority: payload.priority || "medium",
        subtasks: payload.subtasks || [],
        recurrence: payload.recurrence || "none",
        comment_count: 0,
        attachment_count: 0,
        created_at: nowIso,
        updated_at: nowIso,
      }),
    });
    set((s) => ({
      tasks: [
        {
          ...data,
          comment_count: data.comment_count ?? 0,
          attachment_count: data.attachment_count ?? 0,
        },
        ...s.tasks,
      ],
    }));
    if (offline) {
      // Attach the temp id to the outbox row so replay can reconcile.
      // The outbox entry already stored — we mark the client copy so
      // components can show a pending badge.
    }
    return data;
  },

  async updateTask(taskId, patch) {
    // Server IDs never start with `temp_`. If we're patching an optimistic
    // task that hasn't been created server-side yet, keep the change local
    // — replay will read the freshest task state via the store when the
    // POST /tasks entry runs. For simplicity we only try the API when we
    // have a real id.
    const isTemp = typeof taskId === "string" && taskId.startsWith("temp_");
    if (isTemp) {
      set((s) => ({
        tasks: s.tasks.map((t) =>
          t.id === taskId ? { ...t, ...patch, updated_at: new Date().toISOString() } : t,
        ),
      }));
      return { id: taskId, ...patch };
    }
    const { data } = await offlineMutate({
      url: `/tasks/${taskId}`,
      method: "PATCH",
      body: patch,
      buildOptimistic: () => {
        const existing = get().tasks.find((t) => t.id === taskId);
        return {
          ...(existing || {}),
          ...patch,
          id: taskId,
          updated_at: new Date().toISOString(),
        };
      },
    });
    set((s) => ({
      tasks: s.tasks.map((t) =>
        t.id === taskId
          ? {
              ...data,
              comment_count: data.comment_count ?? t.comment_count ?? 0,
              attachment_count: data.attachment_count ?? t.attachment_count ?? 0,
              _pending: data._pending,
            }
          : t,
      ),
    }));
    return data;
  },

  async deleteTask(taskId) {
    await api.delete(`/tasks/${taskId}`);
    set((s) => ({ tasks: s.tasks.filter((t) => t.id !== taskId) }));
  },

  async createList(payload) {
    const { data } = await api.post("/lists", payload);
    set((s) => ({ lists: [...s.lists, data] }));
    return data;
  },

  /**
   * Patch a list's mutable fields (name / color / emoji / order).
   * Backend returns the fresh doc which we merge into local state so the
   * chips + sidebar re-render instantly.
   */
  async updateList(listId, patch) {
    const { data } = await api.patch(`/lists/${listId}`, patch);
    set((s) => ({
      lists: s.lists.map((l) => (l.id === listId ? { ...l, ...data } : l)),
    }));
    return data;
  },

  /** Delete a project group (list). Backend cascades child tasks. */
  async deleteList(listId) {
    await api.delete(`/lists/${listId}`);
    set((s) => ({
      lists: s.lists.filter((l) => l.id !== listId),
      // Also drop tasks that lived in the removed list so the UI doesn't
      // render orphan rows until the next full refetch.
      tasks: s.tasks.filter((t) => t.list_id !== listId),
    }));
  },

  /**
   * Called by the outbox replayer after a queued POST /tasks succeeded on the
   * server. Swap the client-side `temp_...` id for the real server id and drop
   * the `_pending` badge so the UI reflects reality.
   */
  reconcileReplayedTask(realTask, tempTaskFingerprint) {
    set((s) => {
      const idx = s.tasks.findIndex(
        (t) =>
          t._pending &&
          typeof t.id === "string" &&
          t.id.startsWith("temp_") &&
          t.title === tempTaskFingerprint.title &&
          (t.project_id || null) === (tempTaskFingerprint.project_id || null) &&
          (t.list_id || null) === (tempTaskFingerprint.list_id || null),
      );
      if (idx === -1) return s;
      const next = s.tasks.slice();
      next[idx] = {
        ...realTask,
        comment_count: next[idx].comment_count || 0,
        attachment_count: next[idx].attachment_count || 0,
      };
      return { tasks: next };
    });
  },

  /** Drop an optimistic task if its outbox entry was permanently rejected. */
  dropOptimisticTask(match) {
    set((s) => ({
      tasks: s.tasks.filter(
        (t) =>
          !(
            t._pending &&
            typeof t.id === "string" &&
            t.id.startsWith("temp_") &&
            t.title === match.title &&
            (t.project_id || null) === (match.project_id || null)
          ),
      ),
    }));
  },
}));
